import {
  getFirestore, collection, addDoc, getDocs,
  query, orderBy, where, Timestamp, serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from "./firebase";

const db  = getFirestore(app);
const fns = getFunctions(app);

/**
 * submitPicksSecure — calls the submitPickCallable Cloud Function.
 *
 * This is the primary submission path. The Cloud Function:
 *   - Validates balance server-side (can't be bypassed with dev tools)
 *   - Enforces rate limiting (20 picks / 60 seconds)
 *   - Atomically creates pick docs + deducts balance in one transaction
 *
 * Returns: { success: true, pickIds: string[] }
 * Throws:  FirebaseFunctionsError with .code and .message
 *          "functions/resource-exhausted" → rate limited
 *          "functions/failed-precondition" → insufficient balance
 */
export async function submitPicksSecure(userId, picksArray, totalWager, leagueId = null) {
  const callFn = httpsCallable(fns, "submitPickCallable");
  const result = await callFn({ picks: picksArray, totalWager, leagueId });
  return result.data; // { success: true, pickIds: string[] }
}

/**
 * submitPicks — legacy direct Firestore write.
 *
 * Kept as a fallback in case the Cloud Function is unavailable.
 * The client calls submitPicksSecure first; if it fails with a network/
 * unavailable error, the UI can surface this to the user.
 * Balance deduction (applyBalanceDelta) must still be called by the client
 * when using this fallback path.
 */
export async function submitPicks(userId, picksArray) {
  const col = collection(db, "picks", userId, "userPicks");
  const saved = await Promise.all(
    picksArray.map(pick => {
      const odds = (pick.odds != null && pick.odds !== 0) ? pick.odds : null;

      let commenceTime = null;
      if (pick.commenceTime) {
        if (pick.commenceTime instanceof Date) {
          commenceTime = Timestamp.fromDate(pick.commenceTime);
        } else if (typeof pick.commenceTime === "string") {
          commenceTime = Timestamp.fromDate(new Date(pick.commenceTime));
        } else {
          commenceTime = pick.commenceTime;
        }
      }

      return addDoc(col, {
        gameId:         pick.gameId,
        gameLabel:      pick.gameLabel,
        type:           pick.type,
        side:           pick.side,
        line:           pick.line ?? null,
        label:          pick.label,
        sport:          pick.sport    || "",
        sportKey:       pick.sportKey || "",
        commenceTime,
        leagueId:       pick.leagueId || null,
        homeTeamFull:   pick.homeTeamFull || "",
        awayTeamFull:   pick.awayTeamFull || "",
        wager:          pick.wager   || 0,
        odds,
        expectedPayout: pick.expectedPayout || 0,
        ...(pick.isParlay   ? { isParlay:      true }           : {}),
        ...(pick.legs       ? { legs:          pick.legs }      : {}),
        ...(pick.legCount   ? { legCount:      pick.legCount }  : {}),
        ...(pick.combinedOdds != null ? { combinedOdds: pick.combinedOdds } : {}),
        // Persist whether this was placed as a LIVE bet (game already in progress).
        // Required so the stale-void heuristic can judge live bets from submit time
        // instead of game-start time — a live bet's commenceTime is already in the
        // past, so without this flag findStalePicks would void it on next load.
        // Persist player-prop identity + team-total side on the fallback path
        // too. Without these, a prop submitted via the fallback can't be graded
        // (the grader needs playerName/statType to find the stat), and a
        // team_total loses its explicit teamSide and falls back to fragile label
        // inference. The secure CF path already persists all three; this keeps
        // the fallback at parity.
        ...(pick.playerName ? { playerName: pick.playerName } : {}),
        ...(pick.statType   ? { statType:   pick.statType }   : {}),
        ...(pick.teamSide   ? { teamSide:   pick.teamSide }   : {}),
        ...(pick.isLiveBet ? { isLiveBet: true } : {}),
        // Persist the league's scoring format on the fallback path too. The
        // secure CF stamps this from the league doc; without it here, a pickem
        // pick that lands via the fallback (CF timeout/network error) would have
        // no scoringMode field — and settlement treats an absent field as
        // "wager", so it would be graded with payout math against a league that
        // has no ledger. Only written when present so wager picks are unchanged.
        ...(pick.scoringMode ? { scoringMode: pick.scoringMode } : {}),
        status:      "pending",
        points:      null,
        submittedAt: serverTimestamp(),
      });
    })
  );
  return saved.map(d => d.id);
}

export async function loadPicks(userId) {
  const col  = collection(db, "picks", userId, "userPicks");
  const q    = query(col, orderBy("submittedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({
    id: d.id,
    ...d.data(),
    submittedAt: d.data().submittedAt?.toDate?.() ?? new Date(),
  }));
}

// ── BOUNDED READS (fix 2.2, 2026-09-28) ──────────────────────────────────────
// loadPicks() reads a user's ENTIRE history. It was being called every 90 s by
// the root settlement runner (which only needs pending picks) and once per
// member on every league-room open (which only needs that league's picks), so
// Firestore reads grew with every pick anyone ever made. These two queries use
// single-field equality filters, which Firestore indexes automatically -- no
// composite index to deploy. Results are sorted newest-first client-side to
// match loadPicks()' shape.
const sortNewestFirst = (rows) =>
  rows.sort((a, b) => (b.submittedAt?.getTime?.() ?? 0) - (a.submittedAt?.getTime?.() ?? 0));
const toRows = (snap) => snap.docs.map(d => ({
  id: d.id,
  ...d.data(),
  submittedAt: d.data().submittedAt?.toDate?.() ?? new Date(),
}));

export async function loadPendingPicks(userId) {
  const col = collection(db, "picks", userId, "userPicks");
  const snap = await getDocs(query(col, where("status", "==", "pending")));
  return sortNewestFirst(toRows(snap));
}

export async function loadLeaguePicks(userId, leagueId) {
  if (!leagueId) return [];
  const col = collection(db, "picks", userId, "userPicks");
  const snap = await getDocs(query(col, where("leagueId", "==", leagueId)));
  return sortNewestFirst(toRows(snap));
}

// ROUND 8: cancelPicks removed. It deleted pending picks WITHOUT refunding the
// wager debited at submit -- a silent point sink if it were ever wired to a
// button (it had zero call sites). If a cancel feature ships later, it must be
// a Cloud Function that voids + refunds atomically, not a client-side delete.

